import React, { useState, useEffect } from 'react';
import { Package, Search, AlertCircle, Minus, Plus, Save, Loader } from 'lucide-react';

interface InventoryItem {
  inventory_item_id: number;
  part_number: string;
  part_name: string;
  category: string;
  current_stock: number;
  reorder_point: number;
  machine_count: number;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Language support
const translations = {
  en: {
    title: 'Inventory',
    search: 'Search parts...',
    partNumber: 'Part Number',
    partName: 'Part Name',
    stock: 'Stock',
    usedIn: 'Used In',
    machines: 'machines',
    lowStock: 'Low Stock',
    adjustQty: 'Adjust Quantity',
    currentStock: 'Current Stock',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading...',
    noResults: 'No parts found',
    updated: 'Stock updated successfully',
    error: 'Error updating stock'
  },
  es: {
    title: 'Inventario',
    search: 'Buscar piezas...',
    partNumber: 'Número de Pieza',
    partName: 'Nombre de Pieza',
    stock: 'Existencias',
    usedIn: 'Usado En',
    machines: 'máquinas',
    lowStock: 'Existencias Bajas',
    adjustQty: 'Ajustar Cantidad',
    currentStock: 'Existencias Actuales',
    cancel: 'Cancelar',
    save: 'Guardar',
    loading: 'Cargando...',
    noResults: 'No se encontraron piezas',
    updated: 'Existencias actualizadas',
    error: 'Error al actualizar existencias'
  }
};

export const Inventory: React.FC = () => {
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [newStock, setNewStock] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const t = translations[language];

  // Styles
  const styles = {
    container: {
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: '#8b5cf6',
      color: 'white',
      padding: '16px',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    } as React.CSSProperties,
    headerTitle: {
      fontSize: '20px',
      fontWeight: '600',
      flex: 1
    } as React.CSSProperties,
    content: {
      backgroundColor: 'white',
      padding: '24px',
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    } as React.CSSProperties,
    searchBox: {
      position: 'relative',
      marginBottom: '20px'
    } as React.CSSProperties,
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 40px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      outline: 'none'
    } as React.CSSProperties,
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    } as React.CSSProperties,
    itemCard: {
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: 'white'
    } as React.CSSProperties,
    itemCardLowStock: {
      border: '2px solid #f59e0b',
      backgroundColor: '#fef3c7'
    } as React.CSSProperties,
    itemHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '8px'
    } as React.CSSProperties,
    partNumber: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#8b5cf6'
    } as React.CSSProperties,
    partName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '8px'
    } as React.CSSProperties,
    stockBadge: {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '600'
    } as React.CSSProperties,
    stockBadgeGood: {
      backgroundColor: '#d1fae5',
      color: '#065f46'
    } as React.CSSProperties,
    stockBadgeLow: {
      backgroundColor: '#fef3c7',
      color: '#92400e'
    } as React.CSSProperties,
    infoRow: {
      display: 'flex',
      gap: '16px',
      fontSize: '14px',
      color: '#6b7280'
    } as React.CSSProperties,
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      backgroundColor: '#f3f4f6',
      borderRadius: '6px',
      fontSize: '13px'
    } as React.CSSProperties,
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    } as React.CSSProperties,
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      maxWidth: '400px',
      width: '100%',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    } as React.CSSProperties,
    modalHeader: {
      backgroundColor: '#8b5cf6',
      color: 'white',
      padding: '16px',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '18px',
      fontWeight: '600'
    } as React.CSSProperties,
    modalBody: {
      padding: '20px'
    } as React.CSSProperties,
    quantityControl: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginTop: '20px'
    } as React.CSSProperties,
    quantityButton: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      border: '2px solid #e5e7eb',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s'
    } as React.CSSProperties,
    quantityDisplay: {
      flex: 1,
      textAlign: 'center',
      fontSize: '32px',
      fontWeight: '700',
      color: '#1f2937'
    } as React.CSSProperties,
    modalFooter: {
      display: 'flex',
      gap: '12px',
      padding: '16px',
      borderTop: '1px solid #e5e7eb'
    } as React.CSSProperties,
    button: {
      flex: 1,
      padding: '12px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    } as React.CSSProperties,
    buttonCancel: {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    } as React.CSSProperties,
    buttonSave: {
      backgroundColor: '#8b5cf6',
      color: 'white'
    } as React.CSSProperties,
    message: {
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px',
      fontWeight: '500'
    } as React.CSSProperties,
    messageSuccess: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #10b981'
    } as React.CSSProperties,
    messageError: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #ef4444'
    } as React.CSSProperties,
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#9ca3af'
    } as React.CSSProperties,
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    } as React.CSSProperties
  };

  // Fetch inventory
  useEffect(() => {
    fetchInventory();
  }, []);

  // Filter inventory based on search
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredInventory(inventory);
    } else {
      const search = searchText.toLowerCase();
      const filtered = inventory.filter(item =>
        item.part_number.toLowerCase().includes(search) ||
        item.part_name.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search)
      );
      setFilteredInventory(filtered);
    }
  }, [searchText, inventory]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch inventory');

      const data = await response.json();
      setInventory(data.data || []);
      setFilteredInventory(data.data || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setMessage({ type: 'error', text: t.error });
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item: InventoryItem) => {
    setEditingItem(item);
    setNewStock(item.current_stock);
  };

  const handleCloseModal = () => {
    setEditingItem(null);
    setNewStock(0);
    setMessage(null);
  };

  const handleSaveStock = async () => {
    if (!editingItem) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const employee = JSON.parse(localStorage.getItem('employee') || '{}');
      const quantityChange = newStock - editingItem.current_stock;

      // Determine transaction type based on change
      let transactionType = 'adjustment';
      if (quantityChange > 0) transactionType = 'purchase';
      if (quantityChange < 0) transactionType = 'usage';

      const response = await fetch(`${API_BASE_URL}/api/inventory/${editingItem.inventory_item_id}/adjust`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity_change: quantityChange,
          transaction_type: transactionType,
          employee_id: employee.employee_id,
          notes: 'Updated from PWA'
        })
      });

      if (!response.ok) throw new Error('Failed to update stock');

      setMessage({ type: 'success', text: t.updated });

      // Refresh inventory
      await fetchInventory();

      // Close modal after 1 second
      setTimeout(() => {
        handleCloseModal();
      }, 1000);
    } catch (error) {
      console.error('Error updating stock:', error);
      setMessage({ type: 'error', text: t.error });
    } finally {
      setSaving(false);
    }
  };

  const isLowStock = (item: InventoryItem) => {
    return item.current_stock <= item.reorder_point;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Package size={24} />
        <div style={styles.headerTitle}>{t.title}</div>
      </div>

      <div style={styles.content}>
        {/* Search Box */}
        <div style={styles.searchBox}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder={t.search}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div style={styles.emptyState}>
            <Loader size={48} style={{ margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
            <div>{t.loading}</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredInventory.length === 0 && (
          <div style={styles.emptyState}>
            <Package size={48} style={{ margin: '0 auto 16px' }} />
            <div>{t.noResults}</div>
          </div>
        )}

        {/* Inventory Items */}
        {!loading && filteredInventory.map(item => {
          const lowStock = isLowStock(item);
          return (
            <div
              key={item.inventory_item_id}
              style={{
                ...styles.itemCard,
                ...(lowStock ? styles.itemCardLowStock : {})
              }}
              onClick={() => handleItemClick(item)}
            >
              <div style={styles.itemHeader}>
                <div>
                  <div style={styles.partNumber}>{item.part_number}</div>
                  <div style={styles.partName}>{item.part_name}</div>
                </div>
                <div style={{
                  ...styles.stockBadge,
                  ...(lowStock ? styles.stockBadgeLow : styles.stockBadgeGood)
                }}>
                  {item.current_stock}
                </div>
              </div>

              <div style={styles.infoRow}>
                <div style={styles.badge}>
                  {item.category}
                </div>
                {item.machine_count > 0 && (
                  <div style={styles.badge}>
                    <Package size={14} />
                    {t.usedIn} {item.machine_count} {t.machines}
                  </div>
                )}
                {lowStock && (
                  <div style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#92400e' }}>
                    <AlertCircle size={14} />
                    {t.lowStock}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Stock Modal */}
      {editingItem && (
        <div style={styles.modal} onClick={handleCloseModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              {t.adjustQty}
            </div>

            <div style={styles.modalBody}>
              {message && (
                <div style={{
                  ...styles.message,
                  ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
                }}>
                  {message.text}
                </div>
              )}

              <div style={styles.partNumber}>{editingItem.part_number}</div>
              <div style={styles.partName}>{editingItem.part_name}</div>

              <div style={{ marginTop: '16px' }}>
                <span style={styles.label}>{t.currentStock}</span>
                <div style={styles.quantityControl}>
                  <button
                    style={styles.quantityButton}
                    onClick={() => setNewStock(Math.max(0, newStock - 1))}
                    disabled={saving}
                  >
                    <Minus size={20} />
                  </button>

                  <div style={styles.quantityDisplay}>{newStock}</div>

                  <button
                    style={styles.quantityButton}
                    onClick={() => setNewStock(newStock + 1)}
                    disabled={saving}
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.buttonCancel }}
                onClick={handleCloseModal}
                disabled={saving}
              >
                {t.cancel}
              </button>
              <button
                style={{ ...styles.button, ...styles.buttonSave }}
                onClick={handleSaveStock}
                disabled={saving || newStock === editingItem.current_stock}
              >
                {saving ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                {saving ? t.loading : t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
